// Media device loss detection for Deepgram streaming sessions.
// See docs/planning/deepgram_streaming_robustness.md §3 Phase 3 (device change).

export const AUDIO_DEVICE_CHANGED_STATUS = "Audio device changed — re-grant access";

export type AudioDeviceLossReason = "track-ended" | "device-vanished";

export type MicTrackLike = Pick<MediaStreamTrack, "readyState" | "id"> | null | undefined;

export type MediaDeviceLike = Pick<MediaDeviceInfo, "kind" | "deviceId">;

export function isMediaDevicesMonitorSupported(): boolean {
  if (typeof navigator === "undefined") return false;
  const md = navigator.mediaDevices;
  if (!md || typeof md.getUserMedia !== "function") return false;
  return typeof md.addEventListener === "function";
}

export function isMicTrackLive(track: MicTrackLike): boolean {
  return track?.readyState === "live";
}

/** True when the session's input deviceId is still listed (or id unknown). */
export function isActiveInputDevicePresent(
  devices: readonly MediaDeviceLike[],
  deviceId: string | null | undefined,
): boolean {
  if (!deviceId) return true;
  return devices.some((d) => d.kind === "audioinput" && d.deviceId === deviceId);
}

export function shouldRepromptForAudioDevice(args: {
  sessionRunning: boolean;
  track: MicTrackLike;
  activeDeviceStillPresent: boolean;
}): { reprompt: boolean; reason: AudioDeviceLossReason | null } {
  if (!args.sessionRunning) {
    return { reprompt: false, reason: null };
  }
  if (!isMicTrackLive(args.track)) {
    return { reprompt: true, reason: "track-ended" };
  }
  if (!args.activeDeviceStillPresent) {
    return { reprompt: true, reason: "device-vanished" };
  }
  return { reprompt: false, reason: null };
}

export type DeepgramDeviceMonitorDeps = {
  getStream: () => MediaStream | null;
  isRunning: () => boolean;
  onDeviceLost: (reason: AudioDeviceLossReason) => void;
  enumerateDevices?: () => Promise<MediaDeviceInfo[]>;
  log?: (...args: unknown[]) => void;
};

/** Wires `devicechange` + `track.onended` while a session is listening. */
export class DeepgramDeviceMonitor {
  private deps: DeepgramDeviceMonitorDeps | null = null;
  private deviceChangeHandler: (() => void) | null = null;
  private boundTrack: MediaStreamTrack | null = null;
  private trackEndedHandler: (() => void) | null = null;
  private activeDeviceId: string | null = null;

  attach(deps: DeepgramDeviceMonitorDeps): void {
    this.detach();
    if (!isMediaDevicesMonitorSupported()) return;

    this.deps = deps;
    this.deviceChangeHandler = () => {
      void this.checkForDeviceLoss("device-vanished");
    };
    navigator.mediaDevices.addEventListener("devicechange", this.deviceChangeHandler);
  }

  bindTrack(track: MediaStreamTrack | null): void {
    if (this.boundTrack && this.trackEndedHandler) {
      this.boundTrack.removeEventListener("ended", this.trackEndedHandler);
    }
    this.boundTrack = track;
    this.activeDeviceId = track?.getSettings?.().deviceId ?? null;
    if (!track) return;

    this.trackEndedHandler = () => {
      void this.checkForDeviceLoss("track-ended");
    };
    track.addEventListener("ended", this.trackEndedHandler);
  }

  detach(): void {
    if (this.boundTrack && this.trackEndedHandler) {
      this.boundTrack.removeEventListener("ended", this.trackEndedHandler);
    }
    this.boundTrack = null;
    this.trackEndedHandler = null;
    this.activeDeviceId = null;

    if (this.deviceChangeHandler && isMediaDevicesMonitorSupported()) {
      navigator.mediaDevices.removeEventListener("devicechange", this.deviceChangeHandler);
    }
    this.deviceChangeHandler = null;
    this.deps = null;
  }

  private async checkForDeviceLoss(hint: AudioDeviceLossReason): Promise<void> {
    const deps = this.deps;
    if (!deps) return;

    const stream = deps.getStream();
    const track = stream?.getAudioTracks()[0] ?? this.boundTrack ?? null;

    let activeDeviceStillPresent = true;
    if (hint === "device-vanished" || this.activeDeviceId) {
      try {
        const enumerate = deps.enumerateDevices ?? (() => navigator.mediaDevices.enumerateDevices());
        const devices = await enumerate();
        activeDeviceStillPresent = isActiveInputDevicePresent(devices, this.activeDeviceId);
      } catch (err) {
        deps.log?.("[deepgram] enumerateDevices failed during devicechange", err);
      }
    }

    const decision = shouldRepromptForAudioDevice({
      sessionRunning: deps.isRunning(),
      track,
      activeDeviceStillPresent,
    });
    if (decision.reprompt && decision.reason) {
      deps.log?.("[deepgram] audio device loss", decision.reason);
      deps.onDeviceLost(decision.reason);
    }
  }
}
